import { inject, injectable } from "tsyringe";
import { Client } from "../../../tracejs/src/net/jsonrpc/Client";

import { h } from "../../../tracejs/src/utils/JSXFactory";

import { BaseViewModel } from "../common/BaseViewModel";
import { i18n } from "i18next-ko";
import { TracedoGroupManager } from "../../model/TracedoGroupManager";
import { TracedoGroup } from "../../entities/TracedoGroup";
import { SyncEvent } from "ts-events";

/**
 * Tracedo Group Select
 */
@injectable()
export class TracedoGroupSelectLite extends BaseViewModel<any>
{
    /** Event on Group (or nothing) is selected */
    public onSelect: SyncEvent<TracedoGroup|null> = new SyncEvent<TracedoGroup|null>();

	/** Tracedo Group Manager */
	protected tracedoGroupManager: TracedoGroupManager;

    /** Combo with tracedo groups */
    protected groupComboBox: kendo.ui.ComboBox;

    /** Selected group */
    protected selectedGroup: KnockoutObservable<TracedoGroup|null> = ko.observable(null);

    protected caption: string;
	/**
     * Constructor
     *
     * @param rpc RPC
     * @param tracedoGroupManager
     */
	constructor(@inject(Client) rpc: Client, @inject(TracedoGroupManager) tracedoGroupManager: TracedoGroupManager) {
		super(rpc);
		this.tracedoGroupManager = tracedoGroupManager;
	}

    public async startup(): Promise<any>
    {
        await super.startup();
        this.caption = this.settings.caption ?? i18n.t('common.captions.selectGroup');
    }

    public async rendered(): Promise<any>
    {
        this.groupComboBox = this.element.find('.groups-select').kendoComboBox({
            dataTextField: "name",
            dataValueField: "id",
            autoBind: true,
            minLength: 1,
            dataSource: {
                serverFiltering: true,
                transport: {
                    read: async (options: kendo.data.DataSourceTransportReadOptions) => {
                        const search: string = (options.data.filter.filters ?? []).length > 0 ? (options.data.filter.filters[0] as any).value : null;
                        const groups = await this.tracedoGroupManager.get(search);
                        options.success(groups);
                    }
                }
            },
            filter: "contains",
            suggest: true,
			placeholder: i18n.t('common.captions.pleaseSelectGroup'),
            valuePrimitive: true,
            template: '<strong>#= name #</strong><br><small>#= number #</small>',
			noDataTemplate: (e: any) => {
                if(e.instance.text().trim().length <= 0) {
                    return `<div class="missing-group-info-box">${i18n.t('common.captions.noGroupStartTyping')}</div>`;
                }
                const box = jQuery(`
                    <div class="missing-group-info-box">
                        <div>${i18n.t('common.captions.noGroupAddNew')} "${e.instance.text()}"?</div>
                        <br />
                        <button class="btn btn-primary" type="button" data-action="add-new-group">${i18n.t('common.captions.addNewGroup')}</button>
                    </div>
                `);
                box.find('button[data-action=add-new-group]').on('click', () => {
                    this.addNewGroup(e.instance.text());
                });
                return box;
            },
            select: (e) => {
                // if existing group was selected, load group detail
                this.loadGroupDetail(e.dataItem ? e.dataItem.id : null);
            }
        }).data('kendoComboBox');
    }

    /** Read group detail */
    public async loadGroupDetail(id: number|null)
    {
        if(id === null) {
            this.selectedGroup(null);
            this.onSelect.post(null);
        }
        else {
            const group: TracedoGroup = await this.tracedoGroupManager.getOne(id);
            this.selectedGroup(group);
            this.onSelect.post(group);
        }
    }

    public async addNewGroup(name: string): Promise<TracedoGroup>
    {
        const group = await this.tracedoGroupManager.create(name);
        await this.groupComboBox.dataSource.read();
        this.groupComboBox.close();
        this.loadGroupDetail(group.id);
        return group;
    }

	public template = (): HTMLElement => (
        <div className="h-100">
            <div>
                <label className="w-100">
                    <span data-bind="text: $root.caption" className="d-block mb-1"></span>
                    <div className="d-flex">
                        <select className="groups-select" style="width: calc(100% - 200px)"></select>
                        <ko if="$root.selectedGroup()">
                            <strong data-bind="text: $root.selectedGroup().number" className="ms-2 ps-2 pt-1 pb-1 pe-2 k-success-colored"></strong>
                        </ko>
                    </div>
                </label>
            </div>
        </div>
	);

}
